import { cortexPost, cortexHeaders } from "./cortexFetch"

// aio-api's `answer_sources` comes back as a JSON-encoded string (sometimes
// double-encoded, matching the same double json.loads() the kwik-ci bot
// integration already does for the same field - see utils/cortex.py).
export const parseAnswerSources = (raw) => {
    if (!raw) return []
    try {
        let parsed = JSON.parse(raw)
        if (typeof parsed === 'string') parsed = JSON.parse(parsed)
        return Array.isArray(parsed) ? parsed : []
    } catch {
        return []
    }
}

const buildPayload = ({ question, selectedBase, fileConversation, userId, externalId }) => {
    const payload = fileConversation
        ? { file_id: selectedBase, user_id: userId, question }
        : { knowledge_base_id: selectedBase, user_id: userId, question }

    if (externalId)
        payload['external_id'] = externalId

    return payload
}

// Non-streaming ask: POST /ai/questions/ and wait for the full answer.
export const askQuestion = async ({ assistantUrl, accessToken, ...args }) => {
    const payload = buildPayload(args)
    const res = await cortexPost(`${assistantUrl}/ai/questions/`, accessToken, payload)

    if (res.status !== 201)
        throw new Error(`Couldn't ask question, status ${res.status}`)

    return await res.json()
}

const UUID_RE = /\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\b/
const stripSourceLabels = (text) => text
    .replace("[Fontes", "")
    .replace("[Fonte", "")
    .replace("Fontes", "")
    .replace("Fonte", "")

// Streaming ask: POST /ai/questions/stream/question, retried up to 3 times
// on a 502, then read incrementally - mobile drips the answer character by
// character (matching the original widget's typewriter effect), desktop
// applies each chunk as it arrives.
export const askQuestionStreaming = async ({ assistantUrl, accessToken, onStart, onUpdate, onDone, ...args }) => {
    const payload = buildPayload(args)

    let response
    for (let attempt = 0; attempt < 3; attempt++) {
        response = await fetch(`${assistantUrl}/ai/questions/stream/question`, {
            method: 'POST',
            headers: cortexHeaders(accessToken),
            body: JSON.stringify(payload),
        })
        if (response.status !== 502) break
    }

    if (!response.ok && !response.body)
        throw new Error(`Couldn't ask question (stream), status ${response.status}`)

    const reader = response.body.getReader()
    const isMobile = window.innerWidth <= 800

    let uuid = null
    let responseText = ""
    let streamStart = true

    if (isMobile) {
        let responseSegments = []
        let timer = null
        let streamDone = false

        const drip = () => {
            const char = responseSegments.shift()
            if (char !== undefined) {
                responseText = stripSourceLabels(responseText + char)
                onUpdate(uuid, responseText)
            } else if (streamDone) {
                clearInterval(timer)
            }
        }

        // eslint-disable-next-line no-constant-condition
        while (true) {
            const { done, value } = await reader.read()
            if (done) {
                streamDone = true
                break
            }

            const text = new TextDecoder().decode(value)

            if (streamStart) {
                uuid = text.match(UUID_RE)[0]
                const message = text.substring(text.indexOf(']') + 2)
                responseText += message
                onStart(uuid, responseText)
                streamStart = false
                timer = setInterval(drip, 20)
            } else {
                for (let i = 0; i < text.length; i++)
                    responseSegments.push(text.charAt(i))
            }
        }

        // Let the drip timer finish emptying the buffer before finalizing.
        await new Promise((resolve) => {
            const check = setInterval(() => {
                if (responseSegments.length === 0) {
                    clearInterval(check)
                    clearInterval(timer)
                    resolve()
                }
            }, 20)
        })

        onDone(uuid, responseText)
        return
    }

    // eslint-disable-next-line no-constant-condition
    while (true) {
        const { done, value } = await reader.read()
        if (done) {
            onDone(uuid, responseText)
            return
        }

        const text = new TextDecoder().decode(value)

        if (streamStart) {
            uuid = text.match(UUID_RE)[0]
            const message = text.substring(text.indexOf(']') + 2)
            responseText += message
            onStart(uuid, responseText)
            streamStart = false
        } else {
            responseText = stripSourceLabels(responseText + text)
            onUpdate(uuid, responseText)
        }
    }
}
